/**
 * InfinitSales - WhatsApp Business Connection Modal
 * OAuth-based connection with Meta/Facebook SDK
 * 
 * COEXISTENCE MODE (whatsapp_business_app_onboarding)
 * - User keeps WhatsApp Business app on phone
 * - Messages sync between app and API
 * - History sync available (up to 180 days)
 * 
 * Flow:
 * 1. User clicks "Connect"
 * 2. Facebook SDK opens popup
 * 3. User selects to connect existing WA Business app
 * 4. User receives verification code on phone
 * 5. User confirms in the WA Business app
 * 6. User pastes code in browser
 * 7. We receive auth code + WABA data
 * 8. We exchange code for token via backend
 * 9. Backend syncs contacts and message history
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle, 
  Building2,
  ExternalLink,
  RefreshCw,
  Smartphone,
  MessageSquare,
  History,
  Shield,
  ArrowRight,
  Info,
  LogOut,
  Phone,
  User,
  Wifi
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import waBusinessService from '../../services/wa-business/waBusinessService';
import ConnectionHealthPanel from '../common/ConnectionHealthPanel';
import sseService, { SSE_EVENTS } from '../../services/global/sseService';

// Meta App Configuration from environment
const META_APP_ID = process.env.REACT_APP_META_APP_ID || '1833485603931931';
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID || '1463966647970356';

const ConnectionModal = ({ 
  isOpen, 
  onClose, 
  onConnected,
  // NEW PROPS for connection status
  connectionStatus = null, // { connected, phone_number, verified_name, token_expired }
  onDisconnected = null,   // Callback when disconnected
}) => {
  const { colors } = useTheme();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, loading, exchanging, syncing, success, error, disconnecting
  const [accountData, setAccountData] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  
  // Refs
  const metaSignupDataRef = useRef(null);
  // Numero que o usuario esta conectando (opcional): quando a conta da Meta
  // tem mais de um numero e o signup de coexistencia nao manda o
  // phone_number_id, o backend usa este valor para nao conectar o numero
  // errado (WAB_MULTI_NUMBER_WABA_ENABLED). Ref porque a troca do code roda
  // num callback memorizado.
  const [preferredPhone, setPreferredPhone] = useState('');
  const preferredPhoneRef = useRef('');
  const fbInitializedRef = useRef(false);
  const syncCleanupRef = useRef(null);
  const syncTimeoutRef = useRef(null);

  // Check if already connected (from props)
  const isAlreadyConnected = connectionStatus?.connected === true;

  // Reset transient state every time the modal is reopened, so a previous
  // 'success' status doesn't keep the modal in the success view (and hide the
  // disconnect button that lives on the "already connected" view).
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setError(null);
      setSyncProgress(null);
      setLoading(false);
      setShowDisconnectConfirm(false);
      metaSignupDataRef.current = null;
    }
  }, [isOpen]);

  // ============================================
  // FACEBOOK SDK INITIALIZATION
  // ============================================

  useEffect(() => {
    if (!isOpen) return;

    const initFacebookSDK = () => {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: META_APP_ID,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v22.0'
        });
        
        fbInitializedRef.current = true;
        setSdkReady(true);
        console.log('[WA Business] Facebook SDK initialized');
      };

      if (!document.getElementById('facebook-jssdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
          console.log('[WA Business] Facebook SDK script loaded');
        };
        
        script.onerror = () => {
          console.error('[WA Business] Failed to load Facebook SDK');
          setError('Falha ao carregar SDK do Facebook. Recarregue a página.');
        };
        
        document.body.appendChild(script);
      } else if (window.FB) {
        fbInitializedRef.current = true;
        setSdkReady(true);
      }
    };

    initFacebookSDK();
  }, [isOpen]);

  // ============================================
  // POST MESSAGE LISTENER (for WABA data)
  // ============================================

  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.log('[WA Business] Received WA_EMBEDDED_SIGNUP:', data.event, data);
          
          // Handle COEXISTENCE finish event
          if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' || data.event === 'FINISH') {
            const { phone_number_id, waba_id, business_id } = data.data || {};
            
            console.log('[WA Business] Coexistence signup finished:', { 
              phone_number_id, 
              waba_id, 
              business_id,
              event: data.event
            });
            
            metaSignupDataRef.current = { 
              phone_number_id, 
              waba_id, 
              business_id,
              isCoexistence: data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
            };
            
          } else if (data.event === 'CANCEL') {
            // current_step = tela em que o cliente desistiu; error_code/session_id
            // = erro da Meta. É o que o suporte precisa para saber onde travou.
            const { current_step, error_code, error_message, session_id } = data.data || {};
            console.warn('[WA Business] Signup cancelado', { current_step, error_code, error_message, session_id });
            setError(error_message
              ? `A Meta recusou a conexão: ${error_message}${error_code ? ` (${error_code})` : ''}`
              : 'Conexão cancelada pelo usuário');
            setStatus('error');
            setLoading(false);
            
          } else if (data.event === 'ERROR') {
            console.error('[WA Business] Signup error:', data.data);
            setError(data.data?.error_message || 'Erro ao conectar com WhatsApp Business');
            setStatus('error');
            setLoading(false);
          }
        }
      } catch (err) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen]);

  // ============================================
  // OAUTH EXCHANGE
  // ============================================

  const teardownSyncListeners = useCallback(() => {
    if (syncCleanupRef.current) {
      try { syncCleanupRef.current(); } catch (_) { /* noop */ }
      syncCleanupRef.current = null;
    }
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
  }, []);

  const finishConnection = useCallback((data) => {
    teardownSyncListeners();
    setStatus('success');
    setAccountData(data);
    setTimeout(() => {
      onConnected?.(data);
    }, 1500);
  }, [onConnected, teardownSyncListeners]);

  const subscribeToSyncProgress = useCallback((accountData) => {
    teardownSyncListeners();

    setStatus('syncing');
    setSyncProgress({ contacts: 'done', history: 'syncing' });

    const offProgress = sseService.on(SSE_EVENTS.SYNC_PROGRESS, (payload) => {
      if (payload?.source !== 'wa_business') return;
      const pct = payload.meta_progress ?? 0;
      console.log('[WA Business] SYNC_PROGRESS', pct);
      setSyncProgress({
        contacts: 'done',
        history: pct >= 100 ? 'done' : 'syncing',
        progress: pct
      });
    });

    const offCompleted = sseService.on(SSE_EVENTS.SYNC_COMPLETED, (payload) => {
      if (payload?.source !== 'wa_business') return;
      console.log('[WA Business] SYNC_COMPLETED', payload);
      setSyncProgress({ contacts: 'done', history: 'done', progress: 100 });
      finishConnection(accountData);
    });

    const offError = sseService.on(SSE_EVENTS.SYNC_ERROR, (payload) => {
      if (payload?.source !== 'wa_business') return;
      console.warn('[WA Business] SYNC_ERROR — completing without history', payload);
      // Mark history as failed but contacts step still "done" — connection itself
      // succeeded, the user can keep using the app, just without imported history.
      setSyncProgress({ contacts: 'done', history: 'error', progress: 0 });
      finishConnection(accountData);
    });

    syncCleanupRef.current = () => {
      offProgress();
      offCompleted();
      offError();
    };

    // Safety: never leave the user staring at "Sincronizando..." forever.
    // If Meta is silent for 30s (e.g. non-coexistence path that slipped past the
    // frontend flag), declare success so the user can start using the account.
    syncTimeoutRef.current = setTimeout(() => {
      console.warn('[WA Business] Sync silent for 30s — proceeding to success');
      finishConnection(accountData);
    }, 30000);
  }, [finishConnection, teardownSyncListeners]);

  const exchangeCodeForToken = useCallback(async (code) => {
    setStatus('exchanging');

    try {
      // Wait a bit for postMessage data to arrive
      await new Promise(resolve => setTimeout(resolve, 2000));

      const signupData = metaSignupDataRef.current || {};

      console.log('[WA Business] Exchanging code for token...', {
        hasCode: !!code,
        hasWabaId: !!signupData.waba_id,
        hasPhoneNumberId: !!signupData.phone_number_id,
        isCoexistence: signupData.isCoexistence,
      });

      const response = await waBusinessService.exchangeOAuth(
        code,
        signupData.waba_id,
        signupData.phone_number_id,
        signupData.business_id,
        signupData.isCoexistence === true,
        preferredPhoneRef.current || null
      );

      if (response.success) {
        console.log('[WA Business] Account configured successfully');

        if (signupData.isCoexistence) {
          subscribeToSyncProgress(response.data);
        } else {
          finishConnection(response.data);
        }

      } else {
        throw new Error(response.message || 'Erro ao configurar conta');
      }

    } catch (err) {
      console.error('[WA Business] OAuth exchange error:', err);

      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Erro ao conectar WhatsApp Business';

      setError(errorMsg);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [subscribeToSyncProgress, finishConnection]);

  // Cleanup SSE listeners on unmount
  useEffect(() => {
    return () => {
      teardownSyncListeners();
    };
  }, [teardownSyncListeners]);

  // ============================================
  // LAUNCH SIGNUP WITH COEXISTENCE
  // ============================================

  const launchWhatsAppSignup = useCallback(() => {
    if (!window.FB) {
      setError('SDK do Facebook não carregado. Recarregue a página.');
      return;
    }

    // Reset state
    setLoading(true);
    setError(null);
    setStatus('loading');
    setSyncProgress(null);
    metaSignupDataRef.current = null;

    console.log('[WA Business] Launching WhatsApp signup with COEXISTENCE mode...');

    window.FB.login(
      (response) => {
        console.log('[WA Business] FB.login response:', response);
        
        if (response.authResponse && response.authResponse.code) {
          const code = response.authResponse.code;
          console.log('[WA Business] Got auth code, exchanging...');
          exchangeCodeForToken(code);
        } else {
          console.log('[WA Business] No auth code received');
          setError('Autorização negada ou cancelada');
          setStatus('error');
          setLoading(false);
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding', // ← COEXISTENCE MODE!
          sessionInfoVersion: '3',
          // Embedded Signup v4: a Meta desliga v2/v3 em 15/10/2026 e a
          // coexistência não migra sozinha. É o mesmo extras que o Embedded
          // Signup Builder gera (featureType + sessionInfoVersion + version).
          ...(process.env.REACT_APP_META_ES_VERSION
            ? { version: process.env.REACT_APP_META_ES_VERSION }
            : {}),
        }
      }
    );
  }, [exchangeCodeForToken]);

  // ============================================
  // RESET
  // ============================================

  const handleRetry = () => {
    setError(null);
    setStatus('idle');
    setLoading(false);
    setSyncProgress(null);
    setShowDisconnectConfirm(false);
    metaSignupDataRef.current = null;
  };

  // ============================================
  // DISCONNECT
  // ============================================

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);

    try {
      console.log('[WA Business] Disconnecting account...');
      
      await waBusinessService.disconnect();
      
      console.log('[WA Business] Account disconnected successfully');
      
      setShowDisconnectConfirm(false);
      onDisconnected?.();
      onClose();
      
    } catch (err) {
      console.error('[WA Business] Disconnect error:', err);
      
      const errorMsg = 
        err.response?.data?.detail || 
        err.response?.data?.message || 
        err.message ||
        'Erro ao desconectar conta';
      
      setError(errorMsg);
    } finally {
      setDisconnecting(false);
    }
  };

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modal: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '16px',
      maxWidth: '480px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    closeBtn: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      width: '36px',
      height: '36px',
      border: 'none',
      background: colors.bgTertiary,
      color: colors.textSecondary,
      cursor: 'pointer',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      zIndex: 10,
    },
    content: {
      padding: '40px 32px',
    },
    header: {
      textAlign: 'center',
      marginBottom: '28px',
    },
    iconContainer: {
      width: '80px',
      height: '80px',
      backgroundColor: '#25D36615',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      position: 'relative',
    },
    coexistenceBadge: {
      position: 'absolute',
      bottom: '-4px',
      right: '-4px',
      width: '28px',
      height: '28px',
      backgroundColor: '#1877f2',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `3px solid ${colors.bgPrimary}`,
    },
    title: {
      fontSize: '24px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
      margin: 0,
    },
    subtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
      margin: 0,
      lineHeight: 1.5,
    },
    infoBox: {
      backgroundColor: '#1877f210',
      border: '1px solid #1877f230',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px',
    },
    infoBoxTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: 600,
      color: '#1877f2',
      marginBottom: '8px',
    },
    infoBoxText: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: 1.5,
      margin: 0,
    },
    errorBox: {
      backgroundColor: `${colors.danger || '#ef4444'}15`,
      border: `1px solid ${colors.danger || '#ef4444'}30`,
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    errorText: {
      fontSize: '14px',
      color: colors.danger || '#ef4444',
      margin: 0,
      flex: 1,
    },
    features: {
      marginBottom: '28px',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      padding: '14px 0',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    featureIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    featureContent: {
      flex: 1,
    },
    featureTitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    featureDesc: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    stepsContainer: {
      marginBottom: '28px',
    },
    stepsTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: colors.textTertiary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '16px',
    },
    step: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px',
    },
    stepNumber: {
      width: '24px',
      height: '24px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 600,
      color: colors.textSecondary,
      flexShrink: 0,
    },
    stepText: {
      fontSize: '14px',
      color: colors.textSecondary,
      lineHeight: 1.4,
      paddingTop: '2px',
    },
    connectBtn: {
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#25D366',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s',
    },
    connectBtnDisabled: {
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
      cursor: 'not-allowed',
    },
    retryBtn: {
      width: '100%',
      padding: '14px 24px',
      backgroundColor: colors.bgTertiary,
      color: colors.textPrimary,
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '12px',
    },
    // Success styles
    successContainer: {
      textAlign: 'center',
      padding: '20px 0',
    },
    successIcon: {
      width: '80px',
      height: '80px',
      backgroundColor: '#25D36620',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      color: '#25D366',
    },
    successTitle: {
      fontSize: '22px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    successText: {
      fontSize: '15px',
      color: colors.textSecondary,
      marginBottom: '8px',
    },
    successPhone: {
      fontSize: '16px',
      fontWeight: 500,
      color: '#25D366',
    },
    // Syncing styles
    syncContainer: {
      padding: '20px 0',
    },
    syncTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
      textAlign: 'center',
    },
    syncSubtitle: {
      fontSize: '14px',
      color: colors.textSecondary,
      marginBottom: '24px',
      textAlign: 'center',
    },
    syncItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      backgroundColor: colors.bgSecondary,
      borderRadius: '10px',
      marginBottom: '10px',
    },
    syncItemIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    syncItemText: {
      flex: 1,
      fontSize: '14px',
      color: colors.textPrimary,
    },
    syncItemStatus: {
      fontSize: '13px',
      fontWeight: 500,
    },
    // Loading styles
    loadingContainer: {
      textAlign: 'center',
      padding: '40px 20px',
    },
    loadingText: {
      fontSize: '14px',
      color: colors.textSecondary,
      marginTop: '16px',
    },
    loadingSubtext: {
      fontSize: '13px',
      color: colors.textTertiary,
      marginTop: '8px',
    },
    footer: {
      marginTop: '24px',
      textAlign: 'center',
      fontSize: '13px',
      color: colors.textTertiary,
    },
    footerLink: {
      color: '#1877f2',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    },
    // Connected state styles
    connectedContainer: {
      textAlign: 'center',
    },
    connectedIcon: {
      width: '80px',
      height: '80px',
      backgroundColor: '#25D36615',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      position: 'relative',
    },
    connectedBadge: {
      position: 'absolute',
      bottom: '-4px',
      right: '-4px',
      width: '28px',
      height: '28px',
      backgroundColor: '#25D366',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `3px solid ${colors.bgPrimary}`,
    },
    connectedTitle: {
      fontSize: '22px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    connectedSubtitle: {
      fontSize: '14px',
      color: colors.textSecondary,
      marginBottom: '24px',
    },
    accountCard: {
      backgroundColor: colors.bgSecondary,
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '24px',
      textAlign: 'left',
    },
    accountRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px',
    },
    accountRowLast: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '0',
    },
    accountIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: colors.bgTertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    accountLabel: {
      fontSize: '12px',
      color: colors.textTertiary,
      marginBottom: '2px',
    },
    accountValue: {
      fontSize: '15px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    disconnectBtn: {
      width: '100%',
      padding: '14px 24px',
      backgroundColor: 'transparent',
      border: `1px solid ${colors.danger || '#ef4444'}`,
      color: colors.danger || '#ef4444',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s',
    },
    disconnectBtnHover: {
      backgroundColor: `${colors.danger || '#ef4444'}10`,
    },
    confirmOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.bgPrimary,
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      zIndex: 10,
    },
    confirmIcon: {
      width: '64px',
      height: '64px',
      backgroundColor: `${colors.danger || '#ef4444'}15`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    confirmTitle: {
      fontSize: '20px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
      textAlign: 'center',
    },
    confirmText: {
      fontSize: '14px',
      color: colors.textSecondary,
      textAlign: 'center',
      marginBottom: '24px',
      lineHeight: 1.5,
    },
    confirmButtons: {
      display: 'flex',
      gap: '12px',
      width: '100%',
    },
    confirmCancelBtn: {
      flex: 1,
      padding: '12px 20px',
      backgroundColor: colors.bgTertiary,
      border: 'none',
      color: colors.textPrimary,
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    confirmDisconnectBtn: {
      flex: 1,
      padding: '12px 20px',
      backgroundColor: colors.danger || '#ef4444',
      border: 'none',
      color: 'white',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderSyncStatus = (status) => {
    if (status === 'done') {
      return <CheckCircle2 size={18} color="#25D366" />;
    } else if (status === 'syncing') {
      return <Loader2 size={18} color="#1877f2" style={{ animation: 'spin 1s linear infinite' }} />;
    } else {
      return <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: colors.bgTertiary }} />;
    }
  };

  const getSyncStatusText = (status) => {
    if (status === 'done') return 'Concluído';
    if (status === 'syncing') return 'Sincronizando...';
    return 'Aguardando';
  };

  const getSyncStatusColor = (status) => {
    if (status === 'done') return '#25D366';
    if (status === 'syncing') return '#1877f2';
    return colors.textTertiary;
  };

  // ============================================
  // RENDER
  // ============================================

  if (!isOpen) return null;

  // Already connected state - Show account info and disconnect option
  if (isAlreadyConnected && status === 'idle') {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
          
          <div style={styles.content}>
            <div style={styles.connectedContainer}>
              {/* Header */}
              <div style={styles.connectedIcon}>
                <Smartphone size={40} color="#25D366" />
                <div style={styles.connectedBadge}>
                  <Wifi size={14} color="white" />
                </div>
              </div>
              <h2 style={styles.connectedTitle}>WhatsApp Conectado</h2>
              <p style={styles.connectedSubtitle}>
                Confira abaixo o diagnóstico da conexão
              </p>

              {/* Diagnóstico REAL da conexão. Antes esta área afirmava
                  "ativa e recebendo mensagens" só pelo estado salvo no nosso
                  banco: no Gelo Americano isso ficou verdadeiro na tela por 17
                  dias enquanto a Meta não entregava webhook nenhum. */}
              <div style={{ width: '100%', marginBottom: 16 }}>
                <ConnectionHealthPanel canal="wa_business" colors={colors} />
              </div>

              {/* Account Info Card */}
              <div style={styles.accountCard}>
                {connectionStatus.verified_name && (
                  <div style={styles.accountRow}>
                    <div style={styles.accountIcon}>
                      <Building2 size={18} />
                    </div>
                    <div>
                      <div style={styles.accountLabel}>Nome verificado</div>
                      <div style={styles.accountValue}>{connectionStatus.verified_name}</div>
                    </div>
                  </div>
                )}
                
                {connectionStatus.phone_number && (
                  <div style={connectionStatus.verified_name ? styles.accountRowLast : styles.accountRow}>
                    <div style={styles.accountIcon}>
                      <Phone size={18} />
                    </div>
                    <div>
                      <div style={styles.accountLabel}>Telefone</div>
                      <div style={styles.accountValue}>{connectionStatus.phone_number}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div style={{ ...styles.errorBox, marginBottom: '16px' }}>
                  <AlertTriangle size={18} color={colors.danger || '#ef4444'} />
                  <p style={styles.errorText}>{error}</p>
                </div>
              )}

              {/* Disconnect Button */}
              <button
                style={styles.disconnectBtn}
                onClick={() => setShowDisconnectConfirm(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.danger || '#ef4444'}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <LogOut size={18} />
                Desconectar conta
              </button>

              {/* Footer */}
              <div style={{ ...styles.footer, marginTop: '20px' }}>
                <p style={{ fontSize: '12px', color: colors.textTertiary }}>
                  Ao desconectar, você perderá o acesso às mensagens nesta plataforma.
                </p>
              </div>
            </div>

            {/* Disconnect Confirmation Overlay */}
            {showDisconnectConfirm && (
              <div style={styles.confirmOverlay}>
                <div style={styles.confirmIcon}>
                  <AlertTriangle size={32} color={colors.danger || '#ef4444'} />
                </div>
                <h3 style={styles.confirmTitle}>Desconectar WhatsApp?</h3>
                <p style={styles.confirmText}>
                  Todas as mensagens e conversas serão removidas desta plataforma. 
                  Seu WhatsApp Business no celular não será afetado.
                </p>
                
                <div style={styles.confirmButtons}>
                  <button
                    style={styles.confirmCancelBtn}
                    onClick={() => setShowDisconnectConfirm(false)}
                    disabled={disconnecting}
                  >
                    Cancelar
                  </button>
                  <button
                    style={{
                      ...styles.confirmDisconnectBtn,
                      ...(disconnecting ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                    }}
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Desconectando...
                      </>
                    ) : (
                      <>
                        <LogOut size={16} />
                        Desconectar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
          
          <div style={styles.content}>
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>
                <CheckCircle2 size={40} />
              </div>
              <h3 style={styles.successTitle}>Conectado com sucesso!</h3>
              <p style={styles.successText}>
                Seu WhatsApp Business está sincronizado
              </p>
              {accountData?.verified_name && (
                <p style={styles.successPhone}>{accountData.verified_name}</p>
              )}
              {accountData?.phone_number && (
                <p style={{ ...styles.successText, marginTop: '4px', fontSize: '14px' }}>
                  {accountData.phone_number}
                </p>
              )}
              <p style={{ ...styles.successText, marginTop: '16px', fontSize: '13px' }}>
                ✓ Você pode continuar usando o app no celular
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Syncing state
  if (status === 'syncing' && syncProgress) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.content}>
            <div style={styles.syncContainer}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Loader2 
                  size={48} 
                  color="#25D366" 
                  style={{ animation: 'spin 1s linear infinite' }} 
                />
              </div>
              <h3 style={styles.syncTitle}>Sincronizando dados...</h3>
              <p style={styles.syncSubtitle}>
                Estamos importando seus contatos e histórico de mensagens
              </p>
              
              <div style={styles.syncItem}>
                <div style={{ ...styles.syncItemIcon, backgroundColor: '#1877f215' }}>
                  <MessageSquare size={18} color="#1877f2" />
                </div>
                <span style={styles.syncItemText}>Contatos</span>
                <span style={{ ...styles.syncItemStatus, color: getSyncStatusColor(syncProgress.contacts) }}>
                  {getSyncStatusText(syncProgress.contacts)}
                </span>
                {renderSyncStatus(syncProgress.contacts)}
              </div>
              
              <div style={styles.syncItem}>
                <div style={{ ...styles.syncItemIcon, backgroundColor: '#25D36615' }}>
                  <History size={18} color="#25D366" />
                </div>
                <span style={styles.syncItemText}>Histórico de mensagens</span>
                <span style={{ ...styles.syncItemStatus, color: getSyncStatusColor(syncProgress.history) }}>
                  {getSyncStatusText(syncProgress.history)}
                </span>
                {renderSyncStatus(syncProgress.history)}
              </div>
              
              <p style={{ fontSize: '12px', color: colors.textTertiary, textAlign: 'center', marginTop: '20px' }}>
                Mantenha o app WhatsApp Business aberto no celular
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading/Exchanging state
  if (loading && (status === 'loading' || status === 'exchanging')) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <button 
            style={styles.closeBtn} 
            onClick={onClose}
            disabled={status === 'exchanging'}
          >
            <X size={20} />
          </button>
          
          <div style={styles.content}>
            <div style={styles.loadingContainer}>
              <Loader2 
                size={56} 
                color="#25D366" 
                style={{ animation: 'spin 1s linear infinite' }} 
              />
              <p style={styles.loadingText}>
                {status === 'exchanging' 
                  ? 'Configurando sua conta...' 
                  : 'Aguardando autorização...'}
              </p>
              <p style={styles.loadingSubtext}>
                {status === 'loading' && 'Siga as instruções na janela do Facebook'}
                {status === 'exchanging' && 'Isso pode levar alguns segundos'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: Idle/Error state - Show connection options
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button style={styles.closeBtn} onClick={onClose}>
          <X size={20} />
        </button>

        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.iconContainer}>
              <Smartphone size={40} color="#25D366" />
              <div style={styles.coexistenceBadge}>
                <Building2 size={14} color="white" />
              </div>
            </div>
            <h2 style={styles.title}>Conectar WhatsApp Business</h2>
            <p style={styles.subtitle}>
              Conecte seu app e continue usando no celular
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={styles.errorBox}>
              <AlertTriangle size={20} color={colors.danger || '#ef4444'} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {/* Features */}
          <div style={styles.features}>
            <div style={styles.featureItem}>
              <div style={{ ...styles.featureIcon, backgroundColor: '#25D36615' }}>
                <Smartphone size={20} color="#25D366" />
              </div>
              <div style={styles.featureContent}>
                <div style={styles.featureTitle}>Continue usando o app</div>
                <div style={styles.featureDesc}>
                  Seu WhatsApp Business no celular funciona normalmente
                </div>
              </div>
            </div>
            
            <div style={styles.featureItem}>
              <div style={{ ...styles.featureIcon, backgroundColor: '#1877f215' }}>
                <MessageSquare size={20} color="#1877f2" />
              </div>
              <div style={styles.featureContent}>
                <div style={styles.featureTitle}>Mensagens sincronizadas</div>
                <div style={styles.featureDesc}>
                  Envie e receba por ambos - tudo sincronizado
                </div>
              </div>
            </div>
            
            <div style={{ ...styles.featureItem, borderBottom: 'none' }}>
              <div style={{ ...styles.featureIcon, backgroundColor: '#f59e0b15' }}>
                <History size={20} color="#f59e0b" />
              </div>
              <div style={styles.featureContent}>
                <div style={styles.featureTitle}>Histórico importado</div>
                <div style={styles.featureDesc}>
                  Até 180 dias de conversas anteriores
                </div>
              </div>
            </div>
          </div>

          {/* Numero que sera conectado (opcional): decide entre os numeros de
              uma conta da Meta com mais de um */}
          <div style={{ marginBottom: '14px' }}>
            <label
              htmlFor="wab-preferred-phone"
              style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}
            >
              Número que você vai conectar (opcional)
            </label>
            <input
              id="wab-preferred-phone"
              type="tel"
              inputMode="tel"
              value={preferredPhone}
              onChange={(e) => {
                setPreferredPhone(e.target.value);
                preferredPhoneRef.current = e.target.value;
              }}
              placeholder="Ex.: 11 94085-4257"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '14px',
                borderRadius: '10px', border: `1px solid ${colors.border || '#e5e7eb'}`,
                backgroundColor: colors.bgSecondary || colors.bgPrimary, color: colors.textPrimary, outline: 'none',
              }}
            />
            <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
              Só é usado quando a sua conta da Meta tem mais de um número.
            </div>
          </div>

          {/* Connect Button */}
          <button
            style={{
              ...styles.connectBtn,
              ...(!sdkReady ? styles.connectBtnDisabled : {}),
            }}
            onClick={launchWhatsAppSignup}
            disabled={!sdkReady}
          >
            {!sdkReady ? (
              <>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Carregando...
              </>
            ) : (
              <>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/8/85/WhatsApp_Business_icon.png"
                  alt=""
                  style={{ width: '24px', height: '24px' }}
                />
                Conectar WhatsApp Business
                <ArrowRight size={20} />
              </>
            )}
          </button>

          {error && (
            <button style={styles.retryBtn} onClick={handleRetry}>
              <RefreshCw size={18} />
              Tentar novamente
            </button>
          )}

          {/* Footer */}
          <div style={styles.footer}>
            <a 
              href="https://business.whatsapp.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={styles.footerLink}
            >
              Saiba mais sobre WhatsApp Business
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConnectionModal;